import { supabase } from './supabase'

/**
 * Stripe Checkout Service using Supabase Backend
 * Implements dynamic checkout sessions for proper payment flow with redirects
 * Based on: https://docs.stripe.com/payments/checkout/build-integration
 */

export interface CheckoutSessionParams {
  userId: string
  priceId: string
  planName: string
  userEmail?: string
}

export interface CheckoutSessionResult {
  sessionId: string
  url: string
}

export interface CheckoutSession {
  id: string
  payment_status: string
  customer: string
  subscription: string
  metadata: {
    user_id: string
    plan_name: string
  }
}

/**
 * Real Stripe Price IDs - Production Ready
 * These correspond to the actual recurring subscriptions in your Stripe dashboard
 */
export const SUBSCRIPTION_PLANS = {
  trial: {
    name: 'Free Trial',
    price: 0,
    credits: 3,
    teamSeats: 1,
    projects: 1,
    versions: 2,
    priceId: 'price_1S8kyYLXrAZSnIBWihKSzlqQ' // USD Free trial price ID
  },
  starter: {
    name: 'Starter',
    price: 15.90,
    credits: 25,
    teamSeats: 1,
    projects: 3,
    versions: 15,
    priceId: 'price_1S8kyWLXrAZSnIBWzGHhRBSl' // USD Stripe price ID
  },
  pro: {
    name: 'Pro',
    price: 39.90,
    credits: 100,
    teamSeats: 3,
    projects: 10,
    versions: 'Unlimited',
    priceId: 'price_1S8kyULXrAZSnIBWSfHBCY6g' // USD Stripe price ID
  },
  agency: {
    name: 'Agency',
    price: 99.90,
    credits: 350,
    teamSeats: 10,
    projects: 25,
    versions: 'Unlimited',
    priceId: 'price_1S8kyPLXrAZSnIBWUwexWj2m' // USD Stripe price ID
  },
  enterprise: {
    name: 'Enterprise',
    price: 299.90,
    credits: 1000,
    teamSeats: 'Unlimited',
    projects: 'Unlimited',
    versions: 'Unlimited',
    priceId: 'price_1S8kyMLXrAZSnIBW4zm4onSE' // USD Stripe price ID
  }
} as const

export type PlanId = keyof typeof SUBSCRIPTION_PLANS

/**
 * Get the proper base URL for development/production
 */
function getBaseUrl(): string {
  if (typeof window !== 'undefined') {
    // In browser, use current origin (Vite uses 5173 by default)
    return window.location.origin;
  }
  
  // Fallback for SSR or Node.js environments
  return 'http://localhost:5173';
}

/**
 * Create a Stripe checkout session for subscription plans
 * Uses the deployed Supabase Edge Function with real Stripe API integration
 */
export async function createCheckoutSession(params: CheckoutSessionParams): Promise<CheckoutSessionResult> {
  throw new Error('Rulevia is shutting down on July 14, 2026. New subscriptions are no longer accepted.');

  try {
    console.log('🔗 Creating Stripe checkout session:', params)
    
    const { data, error } = await supabase.functions.invoke('stripe-checkout-v2', {
      body: {
        action: 'create_checkout_session',
        params: {
          success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/payment/cancel`,
          mode: 'subscription',
          line_items: [
            {
              price: params.priceId,
              quantity: 1,
            },
          ],
          metadata: {
            user_id: params.userId,
            plan_name: params.planName,
            plan_id: Object.keys(SUBSCRIPTION_PLANS).find(key => 
              SUBSCRIPTION_PLANS[key as keyof typeof SUBSCRIPTION_PLANS].priceId === params.priceId
            ) || 'unknown',
            source: 'rulevia_onboarding',
            type: 'subscription'
          },
          customer_email: params.userEmail
        }
      }
    })

    if (error) {
      console.error('❌ Edge Function error:', error)
      throw new Error(`Failed to create checkout session: ${error.message}`)
    }

    if (!data) {
      throw new Error('No data returned from checkout session creation')
    }

    // **FIX: Handle the actual response format from Edge Function**
    console.log('✅ Checkout session created:', data)

    // Handle different response formats from Edge Function
    let sessionId: string;
    let sessionUrl: string;

    if (data.id && data.url) {
      // New format: { id, url }
      sessionId = data.id;
      sessionUrl = data.url;
    } else if (data.sessionId && data.sessionUrl) {
      // Legacy format: { sessionId, sessionUrl }
      sessionId = data.sessionId;
      sessionUrl = data.sessionUrl;
    } else {
      console.error('❌ Invalid session response:', data)
      throw new Error('Invalid response from Stripe: missing session URL or ID')
    }

    return {
      sessionId: sessionId,
      url: sessionUrl
    }
  } catch (error) {
    console.error('❌ Checkout session creation failed:', error)
    throw new Error(`Failed to create checkout session: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Retrieve checkout session details
 * Used for payment success page fulfillment
 */
export async function retrieveCheckoutSession(sessionId: string): Promise<CheckoutSession> {
  try {
    console.log('🔍 Retrieving Stripe checkout session:', sessionId)
    
    const { data, error } = await supabase.functions.invoke('stripe-checkout-v2', {
      body: {
        action: 'retrieve_checkout_session',
        params: { session_id: sessionId }
      }
    });

    if (error) {
      console.error('❌ Failed to retrieve session:', error)
      throw new Error(`Failed to retrieve checkout session: ${error.message}`)
    }

    console.log('✅ Session retrieved:', data)
    return data
  } catch (error) {
    console.error('❌ Session retrieval failed:', error)
    throw error
  }
}

/**
 * Get plan details by plan name
 */
export function getPlanDetails(planName: string) {
  const plan = SUBSCRIPTION_PLANS[planName as keyof typeof SUBSCRIPTION_PLANS]
  if (!plan) {
    throw new Error(`Unknown plan: ${planName}`)
  }
  return plan
}

/**
 * Get plan details by price ID
 */
export function getPlanByPriceId(priceId: string) {
  return Object.values(SUBSCRIPTION_PLANS).find(plan => plan.priceId === priceId);
}

/**
 * Validate if a price ID exists in our plans
 */
export function isValidPriceId(priceId: string): boolean {
  return Object.values(SUBSCRIPTION_PLANS).some(plan => plan.priceId === priceId);
} 